import { Anchor, Badge, Group, Modal, Progress, Select, Stack, Table, Text, TextInput, Button } from "@mantine/core";
import { ConfirmNote, EmptyStateGuide, HelpButtonGroup, HelpIcon, nextStepToast, notifications, useHelpContext } from "@dehub/theme";
import { ArrowsClockwiseIcon, EyeIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { TrustScoreApi } from "../../../apis";
import type {
  IBuyerTrustScoreSummary,
  TrustScoreLevel,
} from "../../../apis/trust-score";
import { DeliveryActionButton, DeliveryRowAction } from "../../../components/delivery/action-controls";
import { TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { ReturnKPICards } from "../../../components/return/kpi-cards";
import { TRUST_LEVEL_COLOR, TRUST_LEVEL_TONE, TrustLevelBadge, trustLevelLabel } from "../../../components/return/trust-level-badge";
import { CustomTable, ITableRef } from "../../../components/table/customTable";
import { DeniedCard, HelpColumnTitle, ListState, SegmentedTabs } from "../../../components/ui";
import { dateFormat } from "../../../utils/date";
import { message } from "../../../utils/message";

type TFn = (k: string, o?: Record<string, unknown>) => string;

const TIER_ORDER: TrustScoreLevel[] = ["EXCELLENT", "GOOD", "MODERATE", "AT_RISK", "HIGH_RISK"];

const sortOptions = (t: TFn) => [
  { value: "rawScore:asc",           label: t("trustList.sort.scoreAsc") },
  { value: "rawScore:desc",          label: t("trustList.sort.scoreDesc") },
  { value: "returnRatePercent:desc", label: t("trustList.sort.returnRateDesc") },
  { value: "totalReturns:desc",      label: t("trustList.sort.returnCountDesc") },
];

type TabValue = "ALL" | TrustScoreLevel;

/** @dehub/help key namespace of this screen (spec 12 §0). */
const HELP = "order.returns-trust-scores";

/** Count inside a segmented tab (same as Буцаалт, Хүргэлт удирдлага): filled on the active tab. */
function TabCount ({ count, active }: { count: number | undefined; active: boolean }){
  if (count == null) return null;
  return (
    <Badge size="md" radius="xl" color={active ? "dark" : "gray"} variant={active ? "filled" : "light"} ff="monospace">
      {count}
    </Badge>
  );
}

const tabs = (t: TFn): { value: TabValue; label: string }[] => [
  { value: "ALL",       label: t("trustList.all") },
  ...TIER_ORDER.map((l) => ({ value: l as TabValue, label: t(`trustLevel.${l}`) })),
];

export function TrustScoresListPage() {
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const { hasAccess } = usePermission("RETURN_ANALYTICS_VIEW");
  const SORT_OPTIONS = sortOptions(t);
  const TABS = tabs(t);
  const BREADCRUMB = [
    { to: "/", label: t("breadcrumb.home") },
    { to: "/returns", label: t("breadcrumb.returns") },
    { label: t("trustDetail.trustScore") },
  ];
  const [activeTab, setActiveTab] = useState<TabValue>("ALL");
  const [search, setSearch]       = useState("");
  const [sortKey, setSortKey]     = useState("rawScore:asc");
  const [recomputing, setRecomputing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const tableRef = useRef<ITableRef>(null);

  // Tier KPI counts — one benchmark() call (tenant.levelDistribution, counted with the tenant's thresholds).
  const { data: benchmark, mutate: mutateBenchmark } = useSWR(
    hasAccess ? ["trust-scores.benchmark"] : null,
    () => TrustScoreApi.benchmark(),
    { revalidateOnFocus: false }
  );
  const dist = benchmark?.tenant.levelDistribution;
  const tabCount = (tab: TabValue): number | undefined =>
    !dist ? undefined : tab === "ALL" ? TIER_ORDER.reduce((sum, l) => sum + (dist[l] ?? 0), 0) : dist[tab] ?? 0;

  // @dehub/help: the tier tab is the page variant; risky tier counts drive the drawer's «Одоо» cards.
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ when: activeTab, vars: { highRiskCount: dist?.HIGH_RISK ?? 0, atRiskCount: dist?.AT_RISK ?? 0 } });
  }, [activeTab, dist, setHelpContext]);

  const filters = useMemo(
    () => ({
      level : activeTab !== "ALL" ? (activeTab as string) : null,
      search: search || null,
      sort  : sortKey,
    }),
    [activeTab, search, sortKey]
  );

  const handleTabChange = useCallback((value: string | null) => {
    setActiveTab((value as TabValue) ?? "ALL");
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
  }, []);

  // O206 — this button recomputes EVERY buyer (no businessId), so it says so and asks first.
  const onRecompute = useCallback(async () => {
    setRecomputing(true);
    try {
      await TrustScoreApi.recompute();
      tableRef.current?.reload();
      mutateBenchmark();
      setConfirmOpen(false);
      nextStepToast(`${HELP}.toast.recompute`, { fallback: { title: t("trustDetail.successTitle"), message: t("trustDetail.recomputeQueued") } });
    } catch (e) {
      const msg = (e as Error)?.message;
      if (typeof msg === "string" && msg.startsWith("error.")) message.error(msg);
      else notifications.show({ color: "red", title: t("toast.error"), message: t("trustDetail.recomputeError") });
    } finally {
      setRecomputing(false);
    }
  }, [mutateBenchmark, t]);

  if (!hasAccess) {
    return (
      <PageLayout title={t("trustDetail.trustScore")} breadcrumb={BREADCRUMB}>
        <DeniedCard permission="RETURN_ANALYTICS_VIEW" />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t("trustDetail.trustScore")}
      subTitle={t("trustList.subtitle")}
      breadcrumb={BREADCRUMB}
      extra={[
        <HelpButtonGroup key="recompute" helpKey={`${HELP}.action.recompute-all`}>
          <DeliveryActionButton
            data-help-id="trust-recompute-all"
            state={{ allowed: hasAccess, reason: null }}
            variant="default"
            leftSection={<ArrowsClockwiseIcon size={18} />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("trustDetail.recomputeAll")}
          </DeliveryActionButton>
        </HelpButtonGroup>,
      ]}
    >
      <Stack gap="md">
        {/* Tier KPI counts (benchmark levelDistribution) */}
        <ReturnKPICards
          cols={5}
          kpis={TIER_ORDER.map((tier) => ({
            label: trustLevelLabel(tier),
            value: dist ? (dist[tier] ?? 0) : "-",
            tone : TRUST_LEVEL_TONE[tier],
          }))}
        />

        {/* Segmented tabs with counts + «?»: one list-header concept with the other lists. */}
        <Group gap="xs" wrap="nowrap">
          <SegmentedTabs
            value={activeTab}
            onChange={handleTabChange}
            data={TABS.map((tab) => ({
              value: tab.value,
              label: (
                <Group gap={6} wrap="nowrap">
                  {tab.label}
                  <TabCount count={tabCount(tab.value)} active={tab.value === activeTab} />
                </Group>
              ),
            }))}
          />
          <HelpIcon helpKey={`${HELP}.types.level`} />
        </Group>

        <Group justify="space-between" wrap="wrap" gap="sm" align="flex-end">
          <TextInput
            placeholder={t("trustList.searchPlaceholder")}
            aria-label={t("trustList.searchPlaceholder")}
            leftSection={<MagnifyingGlassIcon size={16} />}
            value={search}
            onChange={handleSearchChange}
            w={320}
          />
          <Select
            label={t("trustList.sortLabel")}
            data={SORT_OPTIONS}
            value={sortKey}
            onChange={(v) => setSortKey(v ?? "rawScore:asc")}
            w={260}
            allowDeselect={false}
          />
        </Group>

        <TableFrame>
          <Table.ScrollContainer minWidth={900}>
            <CustomTable
              ref={tableRef}
              name="trust-scores.list"
              colCount={7}
              filters={filters}
              empty={
                // @dehub/help: why the list is empty and what to do (R9); the plain state stays the fallback.
                <EmptyStateGuide
                  helpKey={`${HELP}.empty.main`}
                  when={search || activeTab !== "ALL" ? "noResults" : undefined}
                  fallback={
                    <ListState
                      state={search || activeTab !== "ALL" ? "noResults" : "empty"}
                      title={search || activeTab !== "ALL" ? t("listTable.noResults") : t("trustList.empty")}
                      description={search || activeTab !== "ALL" ? t("listTable.noResultsHint") : t("trustList.emptyHint")}
                    />
                  }
                />
              }
              renderError={(retry) => <ListState state="error" onRetry={retry} />}
              loadData={async (filter) => {
                const f = filter?.filter ?? {};
                const offset = filter?.offset ?? { page: 1, limit: 20 };
                const [sortBy, sortDir] = String(f.sort ?? "rawScore:asc").split(":");
                const res = await TrustScoreApi.list({
                  level  : (f.level as TrustScoreLevel | null) ?? undefined,
                  search : (f.search as string | null) ?? undefined,
                  page   : offset.page,
                  limit  : offset.limit,
                  sortBy : sortBy as "rawScore" | "returnRatePercent" | "totalReturns",
                  sortDir: sortDir as "asc" | "desc",
                });
                return { rows: res?.items ?? [], count: res?.pagination?.total ?? 0 };
              }}
              thead={
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={80}>{t("listTable.col.action")}</Table.Th>
                    <Table.Th>{t("detail.field.business")}</Table.Th>
                    <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-score`}>{t("benchmark.score")}</HelpColumnTitle></Table.Th>
                    <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-level`}>{t("trustList.col.level")}</HelpColumnTitle></Table.Th>
                    <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-rate`}>{t("trustList.col.ordersReturnsRate")}</HelpColumnTitle></Table.Th>
                    <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-dispute-rate`}>{t("benchmark.disputeRate")}</HelpColumnTitle></Table.Th>
                    <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-computed-at`}>{t("trustDetail.computedAt")}</HelpColumnTitle></Table.Th>
                  </Table.Tr>
                </Table.Thead>
              }
              tbody={(rows: IBuyerTrustScoreSummary[]) => (
                <Table.Tbody>
                  {rows.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td>
                        <DeliveryRowAction
                          label={t("breadcrumb.detail")}
                          icon={<EyeIcon />}
                          onClick={() => navigate(`/returns/trust-scores/${item.businessId}`)}
                        />
                      </Table.Td>
                      <Table.Td maw={260}>
                        {/* O203 — an unknown name is null: «-», still a link so the buyer can be opened. */}
                        <Anchor
                          component={Link}
                          to={`/returns/trust-scores/${item.businessId}`}
                          size="sm"
                          c="var(--nw-text)"
                          style={{ textDecoration: "underline" }}
                        >
                          {item.businessName || "-"}
                        </Anchor>
                      </Table.Td>
                      <Table.Td maw={200}>
                        <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)" mb={4}>{`${item.rawScore.toFixed(1)} / 100`}</Text>
                        <Progress
                          value={item.rawScore}
                          color={TRUST_LEVEL_COLOR[item.level]}
                          size="sm"
                          radius="xl"
                          aria-label={t("benchmark.score")}
                        />
                      </Table.Td>
                      <Table.Td>
                        <TrustLevelBadge level={item.level} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">
                          {`${item.totalOrders} / ${item.totalReturns} / ${item.returnRatePercent.toFixed(1)}%`}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">{`${item.disputeRatePercent.toFixed(1)}%`}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)" style={{ whiteSpace: "nowrap" }}>{dateFormat(item.computedAt)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              )}
            />
          </Table.ScrollContainer>
        </TableFrame>
      </Stack>

      {/* Confirm modal 440: this recomputes every buyer */}
      <Modal opened={confirmOpen} onClose={() => setConfirmOpen(false)} title={t("trustDetail.recomputeAllTitle")} size={440}>
        <Stack gap="md">
          <Text size="sm">{t("trustDetail.recomputeAllText")}</Text>
          <ConfirmNote helpKey={`${HELP}.confirm.recompute-all`} mt={0} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmOpen(false)} disabled={recomputing}>{t("resolutionPanel.cancel")}</Button>
            <Button onClick={onRecompute} loading={recomputing}>{t("trustDetail.recomputeAll")}</Button>
          </Group>
        </Stack>
      </Modal>
    </PageLayout>
  );
}
